import BaseTableRender from '@components/BaseTableRender';
import { BaseTextField } from '@components/BaseTextField';
import FilterRoot from '@components/filter/FilterRoot';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { BaseHead, CellOfRow } from '@components/tables';
import { pushError, pushSuccess } from '@components/toast';
import { ptzPresetHttpController } from '@controllers/index';
import { Status } from '@EcamModel/model/PTZPresets';
import useMedia from '@hooks/useMedia';
import { Autorenew } from '@mui/icons-material';
import {
    Box,
    IconButton,
    keyframes,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { BaseSelect, Filter } from '@pages/cameras/list';
import { search } from '@pages/cameras/list/helpers';
import color from '@theme/Colors';
import _ from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { formatDateByTimeZone } from 'src/helpers';

export const status: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Mapped',
        Value: Status.Mapped,
    },
    {
        Id: 2,
        Name: 'Unmapped',
        Value: Status.Unmapped,
    },
];

const statusMapping: { [key in Status]: string } = {
    [Status.Mapped]: 'Mapped',
    [Status.Unmapped]: 'Unmapped',
};

const initPagingStep = {
    page: 0,
    pageSize: 10,
    rows: [],
    total: 0,
    totalPages: 0,
};

export const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export default function ListPreset() {
    const { id: cameraId } = useParams();
    const { isMobileSM, isMediumMD } = useMedia();
    const searchRef = useRef<HTMLInputElement | null>(null);
    const queryClient = useQueryClient();

    const [filter, setFilter] = useState<Filter>({
        page: 1,
    });

    const statusAutocomplete = useStyledAutocomplete({
        list: {
            options: status,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const {
        data: listPreset = initPagingStep,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ['listPreset', cameraId, filter.page, filter.status, filter.search],
        queryFn: async () => {
            const res = await ptzPresetHttpController.list({
                filter: {
                    CameraId: Number(cameraId),
                    Status: filter.status?.Value,
                },
                search: { fields: ['PresetName'], content: filter.search ? filter.search : '' },
                pageSize: 10,
                page: filter.page,
            });
            return res;
        },
        keepPreviousData: true,
    });

    const { mutate: syncPreset, isLoading: isSyncing } = useMutation({
        mutationFn: () => ptzPresetHttpController.syncPtzPresets(Number(cameraId)),
        onSuccess: async () => {
            await refetch();
            queryClient.invalidateQueries(['trailAuditEvent']);
            pushSuccess('Sync preset successfully');
        },
        onError: () => {
            pushError('Sync preset failed');
        },
    });

    const handleResetFilter = () => {
        setFilter({
            page: 1,
            status: undefined,
            search: undefined,
        });
        if (searchRef.current) {
            searchRef.current.value = '';
        }
    };

    const handleOnchangeSearch = useCallback(
        _.debounce((value: string) => {
            setFilter((prev) => ({ ...prev, search: value, page: 1 }));
        }, 500),
        []
    );

    return (
        <Stack spacing={2} padding={2} borderRadius={'0px 16px 16px 16px'} border={'1px solid #E4E7EC'}>
            <Stack mt={2}>
                <FilterRoot
                    isCollapse={isMobileSM ? true : false}
                    rightTitleContent={
                        <Box>
                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                                alignItems="center"
                                onClick={() => {
                                    syncPreset();
                                }}
                            >
                                <Tooltip title="Sync preset" placement="top" arrow>
                                    <IconButton
                                        disabled={isSyncing}
                                        sx={{
                                            borderRadius: '3px',
                                            width: '24px',
                                            height: '24px',
                                            color: 'white',
                                            backgroundColor: color.grey300,
                                            '&.MuiIconButton-root.Mui-disabled': {
                                                backgroundColor: '#eee',
                                            },
                                            'aria-busy': isSyncing ? 'true' : 'false',
                                        }}
                                    >
                                        <Autorenew
                                            sx={{
                                                fontSize: 17,
                                                transition: 'transform 0.3s ease-in-out',
                                                color: 'black',
                                                animation: isSyncing ? `${spin} 0.9s linear infinite` : 'none',
                                            }}
                                            className="rotate-icon"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>
                    }
                    filterChild={
                        <Grid2 container xs={12} gap={2} wrap={isMediumMD ? 'wrap' : 'nowrap'}>
                            <Grid2 xs={12} md={6}>
                                <BaseTextField
                                    placeholder={'Search by preset name'}
                                    inputRef={searchRef}
                                    onChange={(e) => {
                                        handleOnchangeSearch(e.target.value);
                                    }}
                                    maxLength={50}
                                    InputProps={{
                                        endAdornment: (
                                            <FiSearch
                                                color="disabled"
                                                size={16}
                                                style={{ color: 'rgba(0, 0, 0, 0.38)', cursor: 'pointer' }}
                                            />
                                        ),
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={6}>
                                <StyledAutocomplete
                                    {...statusAutocomplete}
                                    getOptionLabel={(o) => o.Name}
                                    label="Status"
                                    value={filter.status}
                                    placeholder="All status"
                                    disabled={isLoading}
                                    onChange={(o) => {
                                        setFilter((prev) => ({ ...prev, status: o, page: 1 }));
                                    }}
                                />
                            </Grid2>
                        </Grid2>
                    }
                    handleResetFilter={handleResetFilter}
                />
            </Stack>

            <Stack mt={3} mb={5}>
                <BaseTableRender
                    paging={listPreset}
                    setPage={(page) => {
                        setFilter((prev) => ({ ...prev, page }));
                    }}
                    isLoading={isLoading}
                    tableComponent={
                        <TableContainer component={Box}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <BaseHead align="left" title="Preset name" />
                                        <BaseHead align="left" title="Coordinates" />
                                        <BaseHead align="left" title="Last updated" />
                                        <BaseHead align="left" title="Status" />
                                        <BaseHead align="left" title="Last synced" />
                                        <BaseHead align="left" title="View name" />
                                        <BaseHead align="left" title="Mapper" />
                                        <BaseHead align="left" title="Last mapped" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {listPreset.rows.map((item) => (
                                        <TableRow key={item.Id!}>
                                            <CellOfRow
                                                value={<Typography>{item.PresetName}</Typography>}
                                                align="left"
                                            />
                                            <CellOfRow
                                                value={
                                                    <Typography>
                                                        {`Pan: ${item.PtzPanPos}, Tilt: ${item.PtzTiltPos}, Zoom: ${item.PtzZoomPos}`}
                                                    </Typography>
                                                }
                                                align="left"
                                                sx={{ width: '20%' }}
                                            />
                                            <CellOfRow
                                                value={
                                                    <Typography>
                                                        {item.LastUpdatedPtz
                                                            ? formatDateByTimeZone(item.LastUpdatedPtz)
                                                            : ''}
                                                    </Typography>
                                                }
                                                align="left"
                                            />
                                            <CellOfRow
                                                value={<Typography>{statusMapping[item.Status]}</Typography>}
                                                align="left"
                                            />
                                            <CellOfRow
                                                value={
                                                    <Typography>
                                                        {item.LastSynced ? formatDateByTimeZone(item.LastSynced) : ''}
                                                    </Typography>
                                                }
                                                align="left"
                                            />
                                            <CellOfRow
                                                value={<Typography>{item.ViewName ?? ''}</Typography>}
                                                align="left"
                                            />
                                            <CellOfRow
                                                value={
                                                    <Typography>
                                                        {item.Mapper?.replace('@ukparkingcontrol.com', '') ?? ''}
                                                    </Typography>
                                                }
                                                align="left"
                                            />
                                            <CellOfRow
                                                value={
                                                    <Typography>
                                                        {item.LastMapped ? formatDateByTimeZone(item.LastMapped) : ''}
                                                    </Typography>
                                                }
                                                align="left"
                                            />
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                />
            </Stack>
        </Stack>
    );
}
